/**
 * Contracts for module "command-center". Owner: growth-os (phase 2). Mounted at /v1/command-center (flag growth_os.command_center).
 * Read model over the Growth Graph: KPIs, recommended next actions and health. Pure reads; acting on a
 * recommendation goes through campaign-os / approvals / autonomy, never from this module.
 */
import { z } from 'zod';
import { IsoDateTimeSchema } from './common';
import { NodeTypeSchema } from './growth-graph';

export const COMMAND_CENTER_FLAG = 'growth_os.command_center';

export const KpiIdSchema = z.enum(['audit_score', 'new_leads', 'qualified_leads', 'campaigns_active', 'competitors_tracked', 'keywords_tracked']);
export const KpiSchema = z.object({
  id: KpiIdSchema,
  /** null = not measured (never a made-up zero) */
  value: z.number().nullable(),
  previous: z.number().nullable(),
  unit: z.enum(['count', 'score', 'percent']),
  /** i18n key under growth-os.command.kpi.* */
  labelKey: z.string(),
  measuredAt: IsoDateTimeSchema.nullable(),
});
export type Kpi = z.infer<typeof KpiSchema>;

export const RecommendationKindSchema = z.enum(['connect_data', 'follow_up_leads', 'fix_audit_finding', 'launch_campaign', 'track_competitor', 'review_campaign']);
export const RecommendationSchema = z.object({
  id: z.string(),
  kind: RecommendationKindSchema,
  priority: z.enum(['high', 'medium', 'low']),
  titleKey: z.string(),
  params: z.record(z.string(), z.union([z.string(), z.number()])).default({}),
  /** graph nodes the recommendation is about */
  evidence: z.array(z.object({ nodeType: NodeTypeSchema, nodeId: z.string() })).max(10).default([]),
  /** where the user goes to act; relative portal path */
  actionPath: z.string().nullable(),
});
export type Recommendation = z.infer<typeof RecommendationSchema>;

export const HealthSchema = z.object({
  level: z.enum(['good', 'attention', 'unknown']),
  sources: z.array(z.object({ source: z.enum(['audit', 'crm', 'whatsapp', 'visibility']), connected: z.boolean(), lastSyncAt: IsoDateTimeSchema.nullable() })),
});

export const CommandCenterSchema = z.object({
  generatedAt: IsoDateTimeSchema,
  kpis: z.array(KpiSchema),
  recommendations: z.array(RecommendationSchema).max(20),
  health: HealthSchema,
});
export type CommandCenter = z.infer<typeof CommandCenterSchema>;
